import { ContentCategories, IconDefinition } from "../types";
import Locale from '../utils/Locale';
import { userAccessLevel } from "../user";

/**
 * THIS IS apps/mobile-app/src/screens/home/timeline/detailed/base.tsx PartProps
 */
export class CardConfig { // The old format
    actionName?: string;
    actionParams?: string;
    cardButtonTitle?: string;
    cardSubTitle?: string;
    cardTitle: string;
    cardDescription: string;
    cardIllustration: string;
    dismissButtonHidden?: boolean;
    detailIcon?: IconDefinition | null = null;
    detailTitle?: string;
    detailDescription?: string;
    detailDescription2?: string;
    detailDescription3?: string;
    detailDescription4?: string;
    detailDescription5?: string;
    detailDescriptionArr?: string[];
    detailScreen?: any;
    historicalTitle?: string;
    historicalDescriptionShort?: string[];
    historicalDescriptionFull?: string[];
    moreButton?: string|boolean;
    dismissButtonTitle?: string | boolean = Locale.gettext("Got it!");
    dismissButtonAction?: Function | AlertActionDescripton | null = null;
    actionButtonTitle?: string | boolean = false;
    actionButtonAction?: Function | AlertActionDescripton | boolean = false;
    /** Minimum user access level to see the button. Button is visilbe for everyone if unset.
     * Note: regardless of this setting, the whole card for the alert might be hidden becasue of access checking in the parent component.
     */
    actionButtonVisibleFor?: userAccessLevel;
    /** Minimum user access level to press the button. Button is pressable for everyone if unset. */
    actionButtonEnabledFor?: userAccessLevel;
    resolveSummary?: string|boolean = false;
}

export class AlertAction {
    type:string;
    screen?:string; // types: navigate
    screenParams?:any = {}; // types: navigate
    deviceUuid?:string; // types: directMethodCall
    method?:string; // types: directMethodCall, apiMethodCall
    params?:string; // types: directMethodCall, apiMethodCall
    url?:string; // types: openUrl
}

export class AlertActionDescripton {
    actions: AlertAction[];
    successConfirm?: string;
    closeAfter?:boolean = true;
}

export class AlertInfo {
    created: Date | undefined;
    updated: Date | undefined;
    /** Compact, easy to understand text for e.g: HomeScreen -> Timeline cards.
     * There we don't want to show scary messages like error codes and such.
     */
    card: {
        /** E.g: Heat Pump Error */
        title: string;
        /** E.g: Heat Pump is reporting an error. */
        description: string;
        illustration?: string;
    } = { title: '', description: '' };
    contentCategories: ContentCategories[] = [];
    /** Detailes e.g: when clicking "More Info" on the HomeScreen -> Timeline card.
     * This might contain tech info too e.g: error codes.
     * This might contain real time info e.g: "Current reading of the sensor: XX"
     * so don't use for screens where we use archived alerts e.g: Alerts and History
     */
    details: {
        /** Title of the detail window. If not set this can be card.title */
        title: string;
        /** Long desc with tech. info. E.g:
         * Heat Pump (Serial port: 2, Bus ID: 1) is reporting an error.
         * Cooling Fail.
         * Outlet is not higher than Inlet. Check flow, check compressor operation, check reversing valve.
         */
        description?: string[];
        icon?: string|IconDefinition;
    } = { title: '' };
    /**
     * Same as `details` but this is for showing historycal data of alerts.
     * Should not contain real time data, e.g: "Current sensor reading: XX"
     */
    historical: {
        title: string,
        descriptionShort?: string[],
        descriptionFull?: string[],
    } = { title: '' };
    dismissButtonTitle: string|boolean = Locale.gettext("Got it!");
    dismissButtonAction?: Function|AlertActionDescripton | null = null;
    actionButtonTitle: string|boolean = false;
    actionButtonAction: Function | AlertActionDescripton | boolean = false;
    actionButtonVisibleFor: userAccessLevel;
    actionButtonEnabledFor: userAccessLevel;
    resolveSummary: string|boolean = false;

    /**
     * Legacy function that was used to migrate Timeline cards into the new AlertLoigic class.
     * @param cardConfig Data format used by Timeline cards.
     * @returns 
     * @deprecated new AlertInfo() should be user directly, instead of generating it by fromCardConfig()
     * For this, first we need an easy way that repalces logic here that fills in missing pops using other props
     * e.g: details.title if not set, this function sets it to card.title. More importantly it generates prop: historical automatically from card and detail props.
     */
    static fromCardConfig(cardConfig: CardConfig): AlertInfo {
        cardConfig = Object.assign(new CardConfig(), cardConfig);
        let newInfo = new AlertInfo();
        
        newInfo.card = {
            title: cardConfig.cardTitle,
            description: cardConfig.cardDescription,
            illustration: cardConfig.cardIllustration,
        };

        newInfo.details = {
            title: cardConfig.detailTitle ?? newInfo.card.title,
            description: [
                ...[
                    cardConfig.detailDescription,
                    cardConfig.detailDescription2,
                    cardConfig.detailDescription3,
                    cardConfig.detailDescription4,
                    cardConfig.detailDescription5
                ],
                ...(cardConfig.detailDescriptionArr || [])
            ].filter(line => !!line),
            icon: cardConfig.detailIcon,
        };

        newInfo.historical = {
            title:
                cardConfig.historicalTitle
                ?? newInfo.card.title,
            descriptionShort:
                cardConfig.historicalDescriptionShort
                ?? (newInfo.card.description ? [ newInfo.card.description ] : undefined)
            ,
            descriptionFull:
                cardConfig.historicalDescriptionFull
                ?? cardConfig.historicalDescriptionShort
                ?? newInfo.details.description
            ,
        };

        newInfo.actionButtonTitle = cardConfig.actionButtonTitle;
        newInfo.actionButtonAction = cardConfig.actionButtonAction;
        newInfo.actionButtonVisibleFor = cardConfig.actionButtonVisibleFor;
        newInfo.actionButtonEnabledFor = cardConfig.actionButtonEnabledFor;
        newInfo.dismissButtonTitle = cardConfig.dismissButtonTitle;
        newInfo.dismissButtonAction = cardConfig.dismissButtonAction;
        newInfo.resolveSummary = cardConfig.resolveSummary;

        return newInfo;
    }
    toCardConfig(): CardConfig {
        return {
            cardTitle: this.card?.title,
            cardDescription: this.card?.description,
            cardIllustration: this.card?.illustration,
            detailIcon: this.details?.icon,
            detailTitle: this.details?.title,
            detailDescription: Array.isArray(this.details?.description) ? this.details.description[0] : this.details?.description,
            detailDescription2: Array.isArray(this.details?.description) ? this.details.description[1] : null,
            detailDescription3: Array.isArray(this.details?.description) ? this.details.description[2] : null,
            detailDescription4: Array.isArray(this.details?.description) ? this.details.description[3] : null,
            detailDescription5: Array.isArray(this.details?.description) ? this.details.description[4] : null,
            historicalTitle: this.historical?.title,
            historicalDescriptionShort: this.historical?.descriptionShort,
            historicalDescriptionFull: this.historical?.descriptionFull,
            dismissButtonTitle: this.dismissButtonTitle,
            dismissButtonAction: this.dismissButtonAction,
            actionButtonTitle: this.actionButtonTitle,
            actionButtonAction: this.actionButtonAction,
            actionButtonVisibleFor: this.actionButtonVisibleFor,
            actionButtonEnabledFor: this.actionButtonEnabledFor,
        }
    }
}

/**
 * An item of the array returned by the Professional.getAlerts / Site.getAlerts RPC
 */
export class AlertRecord {
    id: number;
    uuid: string;
    siteId: number;
    deviceId: number;
    entityUuid: string;
    bodyUuid: string;
    controlUuids: string[] | undefined;
    itemType: AlertItemType | undefined;
    type: AlertType;
    level: AlertLevels|string = "ERROR";
    /** E.g:
     * {
     *     ActualSaltLevel": 100,
     *     DesiredMinimumSaltLevel": 200,
     *     ...
     *     ErrorState: "TOO_HIGH", 
     *     TempertureF: 111
     *     ...
     *     ErrorStartTime: new Date().toISOString(),
     *     DriveErrorState: "PENTAIR_WARNING"
     *     ...
     *     // Or for standardized alerts:
     *     ErrorStartTime: new Date().toISOString(),
     *     ErrorStates: [
     *         {
     *             HeaterErrorState: errorState,
     *             RawErrorCode: "ERROR_CODE",
     *             DateTime: new Date().toISOString()
     *         }
     *     ]
     * }
     */
    data: any = null;
    state: AlertStates|string = "OPEN";
    closedBy: string|number|null = null;
    created: Date = new Date();
    updated: Date = new Date();

    constructor(props:Partial<AlertRecord>) {
        Object.assign(this, props);
        // Possible fix for cases when raw RPC response is passed in that has `created` and `updated` as string.
        // Commented out to avoid re-testing all the alerts. Will make sure passing in Date where really needed.
        /*
        if (this.created && typeof this.created === "string") {
            this.created = new Date(this.created);
        }
        if (this.updated && typeof this.updated === "string") {
            this.updated = new Date(this.updated);
        }
        */
    }
}


export enum AlertType {
    ActuatorContinuallyTurning = "ACTUATOR_CONTINUALLY_TURNING",
    ActuatorNotTurning = "ACTUATOR_NOT_TURNING",
    ActuatorOfflineToSecondary = "ACTUATOR_SECONDARY_OFFLINE",
    ActuatorOvercurrent = "ACTUATOR_OVERCURRENT",
    ActuatorStoppedTooSoon = "ACTUATOR_STOPPED_TOO_SOON",
    AmbientTempError = "AMBIENT_TEMP_ERROR",
    ChemicalsNotWorking = "CHEMICALS_NOT_WORKING",
    ConfigurationIssue = "CONFIGURATION_ISSUE",
    DiskSpaceLow = "DISK_SPACE_LOW",
    DoorLeftOpen = "DOOR_LEFT_OPEN",
    FilterClogged = "FILTER_CLOGGED",
    FreeChlorineHigh = "FREE_CHLORINE_HIGH",
    FreeChlorineLow = "FREE_CHLORINE_LOW",
    Heater = "HEATER",
    HeatPump = "HEAT_PUMP",
    LongDisconnected = "LONG_DISCONNECTED",
    LowSalt = "LOW_SALT",
    MaxBodyOfWaterFillVolume = "MAX_BODY_OF_WATER_FILL_VOLUME",
    McuNotFound = "MCU_NOT_FOUND",
    NoFlow = "NO_FLOW",
    NotResponding = "NOT_RESPONDING",
    NotUpToSpeed = "NOT_UP_TO_SPEED",
    NoWaterFiltered = "NO_WATER_FILTERED",
    OrpHigh = "ORP_HIGH",
    OrpLow = "ORP_LOW",
    PhHigh = "PH_HIGH",
    PhLow = "PH_LOW",
    PumpDrive = "PUMP_DRIVE",
    PumpDryRunProtection = "PUMP_DRY_RUN_PROTECTION",
    PumpInterlockedDueToWaterLevelSensor = "PUMP_INTERLOCKED_DUE_TO_WATER_LEVEL_SENSOR",
    SaltChlorinator = "SALT_CHLORINATOR",
    ThermistorError = "THERMISTOR_ERROR",
    TooManyAlerts = "TOO_MANY_ALERTS",
    TotalChlorineHigh = "TOTAL_CHLORINE_HIGH",
    TotalChlorineLow = "TOTAL_CHLORINE_LOW",
    UnstableDevice = "UNSTABLE_DEVICE",
    WaterLevel = "WATER_LEVEL",
}

export type AlertLevels = "FATAL" | "ERROR" | "WARNING" | "INFO";
export enum AlertLevel {
    Fatal = "FATAL",
    Error = "ERROR",
    Warning = "WARNING",
    Info = "INFO"
}

export type AlertStates = "OPEN" | "CLOSED";
export enum AlertState {
    Open = "OPEN",
    Closed = "CLOSED"
}
export enum AlertItemType {
    Device = "DEVICE",
}


export enum HeaterErrorStates {
    None = "NONE",
    Unknown = "UNKNOWN",
    IgnitionFault = "IGNITION_FAULT",
    WaterPressureSwitch = "WATER_PRESSURE_SWITCH",
    HighLimitSwitch = "HIGH_LIMIT_SWITCH",
    AirFlowSwitch = "AIR_FLOW_SWITCH",
    AutoGasShutoffSwitch = "AUTO_GAS_SHUTOFF_SWITCH",
    IgnitionControlError = "IGNITION_CONTROL_ERROR",
    StackFlueSensorErrorAlarm = "STACK_FLUE_SENSOR_ERROR_ALARM",
    StackFlueSensorOpenAlarm = "STACK_FLUE_SENSOR_OPEN_ALARM",
    StackFlueSensorShortAlarm = "STACK_FLUE_SENSOR_SHORT_ALARM",
    WaterSensorOpenAlarm = "WATER_SENSOR_OPEN_ALARM",
    WaterSensorShortAlarm = "WATER_SENSOR_SHORT_ALARM",
    AirFlowFaultAlarm = "AIR_FLOW_FAULT_ALARM",
    FlameNoCallForHeatAlarm = "FLAME_NO_CALL_FOR_HEAT_ALAR",
    IgnitionLockoutAlarm = "IGNITION_LOCKOUT_ALARM",
    WeakFlameAlarm = "WEAK_FLAME_ALARM",
    RAYPAK_AVIA_INLET_SENSOR_FAULT = "RAYPAK_AVIA_INLET_SENSOR_FAULT",
    RAYPAK_AVIA_INLET_SENSOR_OPEN = "RAYPAK_AVIA_INLET_SENSOR_OPEN",
    RAYPAK_AVIA_INLET_SENSOR_SHORT = "RAYPAK_AVIA_INLET_SENSOR_SHORT",
    RAYPAK_AVIA_OUTLET_SENSOR_OPEN = "RAYPAK_AVIA_OUTLET_SENSOR_OPEN",
    RAYPAK_AVIA_OUTLET_SENSOR_SHORT = "RAYPAK_AVIA_OUTLET_SENSOR_SHORT",
    RAYPAK_AVIA_FLUE_SENSOR_OPEN = "RAYPAK_AVIA_FLUE_SENSOR_OPEN",
    RAYPAK_AVIA_FLUE_SENSOR_SHORT = "RAYPAK_AVIA_FLUE_SENSOR_SHORT",
    RAYPAK_AVIA_FLOW_SENSOR_FAILURE = "RAYPAK_AVIA_FLOW_SENSOR_FAILURE",
    RAYPAK_AVIA_PUMP_FAULT = "RAYPAK_AVIA_PUMP_FAULT",
    RAYPAK_AVIA_FALSE_FLAME = "RAYPAK_AVIA_FALSE_FLAME",
    RAYPAK_AVIA_GAS_VALVE_FAULT = "RAYPAK_AVIA_GAS_VALVE_FAULT",
    RAYPAK_AVIA_PUMP_CONTROLLER_NOT_RESPONDING = "RAYPAK_AVIA_PUMP_CONTROLLER_NOT_RESPONDING",
    RAYPAK_AVIA_WATER_CHEM_BOARD_NOT_RESPONDING = "RAYPAK_AVIA_WATER_CHEM_BOARD_NOT_RESPONDING",
    RAYPAK_AVIA_DIFF_PRESSURE_CLOSED = "RAYPAK_AVIA_DIFF_PRESSURE_CLOSED",
    RAYPAK_AVIA_DIFF_PRESSURE_OPEN = "RAYPAK_AVIA_DIFF_PRESSURE_OPEN",
    RAYPAK_AVIA_INTERNAL_FAULT = "RAYPAK_AVIA_INTERNAL_FAULT",
    RAYPAK_AVIA_EEPROM_FAULT = "RAYPAK_AVIA_EEPROM_FAULT",
    RAYPAK_AVIA_FIREMAN_SWITCH_OPEN = "RAYPAK_AVIA_FIREMAN_SWITCH_OPEN",
    RAYPAK_AVIA_LOW_TEMP_LOCKOUT = "RAYPAK_AVIA_LOW_TEMP_LOCKOUT",
    RAYPAK_AVIA_LOW_VOLTAGE = "RAYPAK_AVIA_LOW_VOLTAGE",
    RAYPAK_AVIA_FLAME_LOST = "RAYPAK_AVIA_FLAME_LOST",
    RAYPAK_AVIA_FLOW_SWITCH_OPEN = "RAYPAK_AVIA_FLOW_SWITCH_OPEN",
    RAYPAK_AVIA_VENT_SWITCH_OPEN = "RAYPAK_AVIA_VENT_SWITCH_OPEN",
    RAYPAK_AVIA_HIGH_LIMIT_1_OPEN = "RAYPAK_AVIA_HIGH_LIMIT_1_OPEN",
    RAYPAK_AVIA_HIGH_LIMIT_2_OPEN = "RAYPAK_AVIA_HIGH_LIMIT_2_OPEN",
    RAYPAK_AVIA_CABINET_TEMP_LIMIT = "RAYPAK_AVIA_CABINET_TEMP_LIMIT",
    RAYPAK_AVIA_AIR_PRESSURE_SWITCH_OPEN = "RAYPAK_AVIA_AIR_PRESSURE_SWITCH_OPEN",
    RAYPAK_AVIA_FAN_LOCKOUT = "RAYPAK_AVIA_FAN_LOCKOUT",
    RAYPAK_AVIA_DIFF_SWITCH_FAULT = "RAYPAK_AVIA_DIFF_SWITCH_FAULT",
    RAYPAK_AVIA_IGNITION_FAULT = "RAYPAK_AVIA_IGNITION_FAULT",
    RAYPAK_AVIA_GAS_VALVE_FAILED = "RAYPAK_AVIA_GAS_VALVE_FAILED",
    RAYPAK_AVIA_IGNITION_LOCKOUT = "RAYPAK_AVIA_IGNITION_LOCKOUT",
    RAYPAK_AVIA_FLUE_TEMP_HIGH_LIMIT = "RAYPAK_AVIA_FLUE_TEMP_HIGH_LIMIT",
    RAYPAK_AVIA_REMOTE_PORTS_DISAGREE = "RAYPAK_AVIA_REMOTE_PORTS_DISAGREE",
    RAYPAK_AVIA_OUTLET_LIMIT = "RAYPAK_AVIA_OUTLET_LIMIT",
    RAYPAK_AVIA_LOW_FLOW_FAULT = "RAYPAK_AVIA_LOW_FLOW_FAULT",
    RAYPAK_AVIA_HIGH_FLOW_FAULT = "RAYPAK_AVIA_HIGH_FLOW_FAULT",
    RAYPAK_AVIA_FLUE_TEMP_LOW_LIMIT = "RAYPAK_AVIA_FLUE_TEMP_LOW_LIMIT",
    RAYPAK_AVIA_FLUE_TEMP_EXTREME_LOW_LIMIT = "RAYPAK_AVIA_FLUE_TEMP_EXTREME_LOW_LIMIT",
    RAYPAK_AVIA_WATER_TEMP_SENSOR_MISPLACE = "RAYPAK_AVIA_WATER_TEMP_SENSOR_MISPLACE",
}

export enum HeatPumpErrorStates {
    HighWaterTemperature = "HIGH_WATER_TEMPERATURE",
    LowWaterTemperature = "LOW_WATER_TEMPERATURE",
    LowWaterFlow = "LOW_WATER_FLOW",
    PoolAndSpaInputsAreBothEnabled = "POOL_AND_SPA_INPUTS_ARE_BOTH_ENABLED",
    WaterTemperatureSensorOpen = "WATER_TEMPERATURE_SENSOR_OPEN",
    WaterTemperatureSensorShorted = "WATER_TEMPERATURE_SENSOR_SHORTED",
    DefrostTemperatureSensorOpen = "DEFROST_TEMPERATURE_SENSOR_OPEN",
    DefrostTemperatureSensorShorted = "DEFROST_TEMPERATURE_SENSOR_SHORTED",
    Brownout = "BROWNOUT",
    HighRefrigerant = "HIGH_REFRIGERANT",
    LowRefrigerant = "LOW_REFRIGERANT",
    FiveAlarmsInAnHour = "FIVE_ALARMS_IN_AN_HOUR",
    LowAmbientTemperature = "LOW_AMBIENT_TEMPERAURE",
    AirFlowSwitch = "AIR_FLOW_SWITCH",
    IcmFault = "ICM_FAULT",
    AutomaticGasShutoff = "AUTOMATIC_GAS_SHUTOFF",
    StackFlueHighTemp = "STACK_FLUE_HIGH_TEMP",
    StackFlueOpenShort = "STACK_FLUE_OPEN_SHORT",
    StackFlueRunaway = "STACK_FLUE_RUNAWAY",
    FreezeWarning = "FREEZE_WARNING",
    CondensateFilter = "CONDENSATE_FILTER",
    CondensateFloatSwitch = "CONDENSATE_FLOAT_SWITCH",
    ThermalFuse = "THERMAL_FUSE",
    HighLimitSwitch = "HIGH_LIMIT_SWITCH",
    SuctionTemperatureSensorOpenShort = "SUCTION_TEMPERATURE_SENSOR_OPEN_SHORT",
    Defrosting = "DEFROSTING",
    SilentMode = "SILENT_MODE",
    HighPressureSwitch = "HIGH_PRESSURE_SWITCH",
    LowPressureSwitch = "LOW_PRESSURE_SWITCH",
    TemperatureSensor = "TEMPERATURE_SENSOR",
    CommunicationError = "COMM_ERROR",
    
    RAYPAK_CWV_IN_SENSOR_FAULT = "RAYPAK_CWV_IN_SENSOR_FAULT",
    RAYPAK_CWV_IN_SENSOR_OPEN = "RAYPAK_CWV_IN_SENSOR_OPEN",
    RAYPAK_CWV_IN_SENSOR_SHORT = "RAYPAK_CWV_IN_SENSOR_SHORT",
    RAYPAK_CWV_OUT_SENSOR_OPEN = "RAYPAK_CWV_OUT_SENSOR_OPEN",
    RAYPAK_CWV_OUT_SENSOR_SHORT = "RAYPAK_CWV_OUT_SENSOR_SHORT",
    RAYPAK_CWV_AMBIENT_SEN_OPEN = "RAYPAK_CWV_AMBIENT_SEN_OPEN",
    RAYPAK_CWV_AMBIENT_SEN_SHRT = "RAYPAK_CWV_AMBIENT_SEN_SHRT",
    RAYPAK_CWV_FLOW_SENSOR_FAIL = "RAYPAK_CWV_FLOW_SENSOR_FAIL",
    RAYPAK_CWV_CHECK_PUMP_FAULT = "RAYPAK_CWV_CHECK_PUMP_FAULT",
    RAYPAK_CWV_COIL_SENSOR_OPEN = "RAYPAK_CWV_COIL_SENSOR_OPEN",
    RAYPAK_CWV_COIL_SEN_SHORT = "RAYPAK_CWV_COIL_SEN_SHORT",
    RAYPAK_CWV_PUMP_CTL_FAIL = "RAYPAK_CWV_PUMP_CTL_FAIL",
    RAYPAK_CWV_WCHEM_BRD_FAIL = "RAYPAK_CWV_WCHEM_BRD_FAIL",
    RAYPAK_CWV_INTERNAL_FAULT = "RAYPAK_CWV_INTERNAL_FAULT",
    RAYPAK_CWV_EEPROM_FAULT = "RAYPAK_CWV_EEPROM_FAULT",
    RAYPAK_CWV_LOW_TEMP_LOCKOUT = "RAYPAK_CWV_LOW_TEMP_LOCKOUT",
    RAYPAK_CWV_HI_PRESS_TRIP = "RAYPAK_CWV_HI_PRESS_TRIP",
    RAYPAK_CWV_LO_PRESS_TRIP = "RAYPAK_CWV_LO_PRESS_TRIP",
    RAYPAK_CWV_HI_PRES_LOCKOUT = "RAYPAK_CWV_HI_PRES_LOCKOUT",
    RAYPAK_CWV_LO_PRES_LOCKOUT = "RAYPAK_CWV_LO_PRES_LOCKOUT",
    RAYPAK_CWV_BROWNOUT = "RAYPAK_CWV_BROWNOUT",
    RAYPAK_CWV_CLOCK_FAULT = "RAYPAK_CWV_CLOCK_FAULT",
    RAYPAK_CWV_HI_TEMP_TRIP = "RAYPAK_CWV_HI_TEMP_TRIP",
    RAYPAK_CWV_HI_TEMP_LOCKOUT = "RAYPAK_CWV_HI_TEMP_LOCKOUT",
    RAYPAK_CWV_REMOTE_ERROR = "RAYPAK_CWV_REMOTE_ERROR",
    RAYPAK_CWV_WATER_PS_OPEN = "RAYPAK_CWV_WATER_PS_OPEN",
    RAYPAK_CWV_OUTSIDE_LOCKOUT = "RAYPAK_CWV_OUTSIDE_LOCKOUT",
    RAYPAK_CWV_HI_DELTA_T_FAULT = "RAYPAK_CWV_HI_DELTA_T_FAULT",
    RAYPAK_CWV_HEATING_FAIL = "RAYPAK_CWV_HEATING_FAIL",
    RAYPAK_CWV_COOLING_FAIL = "RAYPAK_CWV_COOLING_FAIL",
    RAYPAK_CWV_SOLAR_SEN_OPEN = "RAYPAK_CWV_SOLAR_SEN_OPEN",
    RAYPAK_CWV_SOLAR_SEN_SHORT = "RAYPAK_CWV_SOLAR_SEN_SHORT",
    RAYPAK_CWV_DEFROST_LOCKOUT = "RAYPAK_CWV_DEFROST_LOCKOUT",
    RAYPAK_CWV_AUXINPUT_WARNING = "RAYPAK_CWV_AUXINPUT_WARNING",
    RAYPAK_CWV_AUX_INPUT_FAULT = "RAYPAK_CWV_AUX_INPUT_FAULT",
    RAYPAK_CWV_SENSOR_MISPLACED = "RAYPAK_CWV_SENSOR_MISPLACED",

    NONE = "NONE",
    Unknown = "UNKNOWN",
}

export enum PumpDriveErrorStates {
    None = "NONE",
    Unknown = "UNKNOWN",
    ConfigurationError = "CONFIGURATION_ERROR",
    Stalled = "STALLED",
    HeatsinkOverheat = "HEATSINK_OVERHEAT",
    OverCurrent = "OVERCURRENT",
    PowerOut = "POWER_OUT",
    PrimingDry = "PRIMING_DRY",
    InvalidSpeedSettings = "INVALID_SPEED_SETTINGS",
    PressureLimit = "PRESSURE_LIMIT",
    DcVoltageHigh = "DC_VOLTAGE_HIGH",
    DcVoltageLow = "DC_VOLTAGE_LOW",
    DriveOverheat = "DRIVE_OVERHEAT",
    StopButtonPressed = "STOP_BUTTON_PRESSED",
    Processor = "PROCESSOR",
    KeyPad = "KEYPAD",
    DataFlash = "DATA_FLASH",
    LowPower = "LOW_POWER",
    LossOfPhase = "LOSS_OF_PHASE",
    Svrs = "SVRS",
    CavitationNonCriticalAlert = "CAVITATION_NON_CRITICAL_ALERT",
    InternalCommunicationError = "INTERNAL_COMMUNICATION_ERROR",
    Transformer = "TRANSFORMER",
    NoFlow = "NO_FLOW",
    LimitWarning = 'LIMIT_WARNING',
    PentairWarning = 'PENTAIR_WARNING',
    AcVoltageLow = "AC_VOLTAGE_LOW"
}

export enum DosingErrorStates {
    FailedDoseRate = "FAILED_DOSE_RATE",
    Overfeed = "OVERFEED"
}

export enum AlertCategories {
    OTHER = "OTHER", 
    EQUIPMENT_FAILURE = "EQUIPMENT_FAILURE",
    WATER_QUIALITY = "WATER_QUIALITY",
}

export const AlertCategoriesLocalized = {
    [ AlertCategories.EQUIPMENT_FAILURE ]: Locale.gettext('Equipment Failure'),
    [ AlertCategories.WATER_QUIALITY ]: Locale.gettext('Water Quality'),
    [ AlertCategories.OTHER ]: Locale.gettext('Other'),
}